import React, { useState } from "react";
import { ChevronDown, ChevronRight, Eye, Car } from "lucide-react";

const STATUS_LABELS = { nou: "Nou", in_verificare: "In verificare", acceptat: "Acceptat", in_lucru: "In lucru", finalizat: "Finalizat", respins: "Respins" };
const STATUS_COLORS = {
  nou: "bg-blue-500/20 text-blue-400",
  in_verificare: "bg-yellow-500/20 text-yellow-400",
  acceptat: "bg-emerald-500/20 text-emerald-400",
  in_lucru: "bg-purple-500/20 text-purple-400",
  finalizat: "bg-green-500/20 text-green-400",
  respins: "bg-red-500/20 text-red-400"
};

const DosarRow = ({ dosar, onView }) => (
  <tr className="hover:bg-gray-700/50">
    <td className="px-4 py-3 text-sm font-mono text-emerald-400">{dosar.numar_dosar}</td>
    <td className="px-4 py-3 text-sm text-white">{dosar.nume_prenume}</td>
    <td className="px-4 py-3 text-sm text-gray-300">{dosar.telefon}</td>
    <td className="px-4 py-3 text-sm text-gray-300">{dosar.nr_auto || "-"}</td>
    <td className="px-4 py-3 text-sm text-gray-300">{dosar.marca_model || "-"}</td>
    <td className="px-4 py-3 text-sm text-gray-300">{dosar.asigurator || "-"}</td>
    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[dosar.status]}`}>{STATUS_LABELS[dosar.status]}</span></td>
    <td className="px-4 py-3 text-sm text-gray-300">{dosar.suma_despagubire ? `${dosar.suma_despagubire} RON` : "-"}</td>
    <td className="px-4 py-3 text-sm">{dosar.discount > 0 ? <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">-{dosar.discount}%</span> : "-"}</td>
    <td className="px-4 py-3">
      <button onClick={() => onView(dosar)} className="p-1 hover:bg-emerald-500/20 rounded text-emerald-400" data-testid={`view-dosar-${dosar.numar_dosar}`}>
        <Eye className="w-4 h-4" />
      </button>
    </td>
  </tr>
);

const ConsilierGroup = ({ consilier, dosare, onViewDosar }) => {
  const [expanded, setExpanded] = useState(false);
  const consilierDosare = dosare.filter(d => d.consilier_id === consilier.id);
  const finalizate = consilierDosare.filter(d => d.status === "finalizat").length;
  const active = consilierDosare.filter(d => !["finalizat", "respins"].includes(d.status)).length;
  const totalSuma = consilierDosare.reduce((sum, d) => sum + (d.suma_despagubire || 0), 0);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid={`consilier-group-${consilier.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-5 hover:bg-gray-750 transition-colors text-left">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 font-bold text-sm">
            {(consilier.nume || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="text-white font-semibold">{consilier.nume}</h3>
            <p className="text-gray-400 text-sm">{consilier.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center hidden sm:block">
            <p className="text-white font-bold">{consilierDosare.length}</p>
            <p className="text-gray-500 text-xs">Total</p>
          </div>
          <div className="text-center hidden sm:block">
            <p className="text-emerald-400 font-bold">{active}</p>
            <p className="text-gray-500 text-xs">Active</p>
          </div>
          <div className="text-center hidden sm:block">
            <p className="text-blue-400 font-bold">{finalizate}</p>
            <p className="text-gray-500 text-xs">Finalizate</p>
          </div>
          <div className="text-center hidden sm:block">
            <p className="text-amber-400 font-bold">{totalSuma > 0 ? `${totalSuma.toLocaleString('ro-RO')}` : "-"}</p>
            <p className="text-gray-500 text-xs">RON</p>
          </div>
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-gray-700">
          {consilierDosare.length === 0 ? (
            <p className="p-5 text-gray-500 text-sm text-center">Niciun dosar atribuit</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Dosar</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Client</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Telefon</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Nr. Auto</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Marca/Model</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Asigurator</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Suma</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Discount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Detalii</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {consilierDosare.map(d => <DosarRow key={d.id} dosar={d} onView={onViewDosar} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DosarePerConsilier = ({ consilieri, dosare, onViewDosar }) => {
  const neatribuite = dosare.filter(d => !d.consilier_id || !consilieri.some(c => c.id === d.consilier_id));

  return (
    <div className="space-y-4" data-testid="dosare-per-consilier">
      {consilieri.map(c => (
        <ConsilierGroup key={c.id} consilier={c} dosare={dosare} onViewDosar={onViewDosar} />
      ))}
      {neatribuite.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <h3 className="text-amber-400 font-semibold mb-3">Dosare neatribuite ({neatribuite.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Dosar</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Client</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Telefon</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Nr. Auto</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Marca/Model</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Asigurator</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Suma</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Discount</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Detalii</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {neatribuite.map(d => <DosarRow key={d.id} dosar={d} onView={onViewDosar} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DosarePerConsilier;
